import { type ReactElement, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAccessControlGuard } from '$shared/custom-hooks/useAccessControlGuard';
import { useTokenAttributes } from '$shared/custom-hooks/useTokenAttributes';

import { FeatureIsEnabled } from '$components/feature-toogle/feature-is-enabled';
import { ConfirmDeleteNotiRuleDialog } from '$features/notifications/detail/confirm-delete-noti-rule-dialog';
import { DeleteNotificationRuleErrorDialog } from '$features/notifications/detail/delete-notification-rule-error-dialog';
import { DeleteNotificationRuleSuccessDialog } from '$features/notifications/detail/delete-notification-rule-success-dialog';
import { NotificationDevicesInfo } from '$features/notifications/detail/notification-detail-devices';
import { NotificationDetailFooter } from '$features/notifications/detail/notification-detail-footer';
import { NotificationDetailHeader } from '$features/notifications/detail/notification-detail-header';
import { NotificationDetailLifecycle } from '$features/notifications/detail/notification-detail-lifecycle';
import { useArchiveNotificationRule } from '$features/notifications/notification-rules.api';
import { NotificationRuleDetailData } from '$features/notifications/types';

import { NotificationDetailCondition } from './notification-detail-condition';
import { NotificationDetailCreatedInfo } from './notification-detail-created-info';
import { NotificationDetailRecipient } from './notification-detail-recipient';
import styles from './notification-detail.module.scss';

export interface NotificationRuleDetailProps {
  data: NotificationRuleDetailData;
  onEditDialogOpen: () => void;
}

export function NotificationRuleDetail({
  data,
  onEditDialogOpen,
}: NotificationRuleDetailProps): ReactElement {
  const [presentingConfirmDeleteDialog, setPresentingConfirmDeleteDialog] =
    useState(false);
  const [presentingDeleteSuccessDialog, setPresentingDeleteSuccessDialog] =
    useState(false);
  const [presentingRetryDeleteDialog, setPresentingRetryDeleteDialog] = useState(false);

  const navigate = useNavigate();

  const errorsFound = data.devices.errors.length > 0;

  const handleArchiveNotificationRule = useCallback(() => {
    setPresentingConfirmDeleteDialog(true);
  }, []);

  const { mutateAsync } = useArchiveNotificationRule();

  const handleConfirmDelete = useCallback(async () => {
    setPresentingConfirmDeleteDialog(false);
    try {
      await mutateAsync(data.id);
      setPresentingRetryDeleteDialog(false);
      setPresentingDeleteSuccessDialog(true);
    } catch (error) {
      // eslint-disable-next-line no-console -- for debugging
      console.error(error);
      setPresentingRetryDeleteDialog(true);
    }
  }, []);

  const handleSuccessDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setPresentingDeleteSuccessDialog(false);
      navigate('/notifications/notifications-rules');
    }
  }, []);

  const { email: emailFromToken } = useTokenAttributes(['email']);
  const canArchiveOwn = useAccessControlGuard('CHANGE_LIFECYCLE_OWN_NOTIFICATION_RULES');
  const canArchiveAll = useAccessControlGuard(
    'CHANGE_LIFECYCLE_OTHERS_NOTIFICATION_RULES',
  );
  const canArchive =
    (canArchiveOwn && data?.createdBy === emailFromToken) || canArchiveAll;

  return (
    <>
      <div
        className={styles.notificationDetailMasthead}
        data-testid="device-detail-header"
      >
        <NotificationDetailHeader notificationRule={data} />
      </div>
      <div className="sidecar__details box box-remove-padding">
        <div className="sidecar__outlet-wrapper">
          <NotificationDevicesInfo
            devices={data.devices.details}
            fetchError={errorsFound}
          />
          <NotificationDetailCondition
            tags={data.tags}
            categories={data.categories}
            devices={data.devices.details}
            selectedDeviceType={data.selectedDeviceType}
          />
          <NotificationDetailRecipient
            escalationLayers={data.escalationLayers || []}
            tags={data.tags ?? []}
            notificationCadence={data.notificationCadence}
            notificationSendDelaySeconds={data.notificationSendDelaySeconds}
          />
          <FeatureIsEnabled feature="ESEVNLS_4596_NOTIFICATION_TYPES">
            <NotificationDetailLifecycle notificationTypes={data.notificationTypes} />
          </FeatureIsEnabled>
          <NotificationDetailCreatedInfo data={data} />
          <NotificationDetailFooter
            canArchive={canArchive}
            onEditDialogOpen={onEditDialogOpen}
            onArchiveNotificationRule={handleArchiveNotificationRule}
            createdBy={data.createdBy || ''}
          />
        </div>
      </div>

      <ConfirmDeleteNotiRuleDialog
        isOpen={presentingConfirmDeleteDialog}
        onClose={() => setPresentingConfirmDeleteDialog(false)}
        onConfirmDelete={handleConfirmDelete}
        name={data.name}
      />

      <DeleteNotificationRuleSuccessDialog
        isOpen={presentingDeleteSuccessDialog}
        onClose={() => handleSuccessDialogClose(false)}
        name={data.name}
      />

      <DeleteNotificationRuleErrorDialog
        isOpen={presentingRetryDeleteDialog}
        onClose={() => setPresentingRetryDeleteDialog(false)}
        onTryAgain={handleConfirmDelete}
      />
    </>
  );
}
